import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';

const formatWhen = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const formatRemain = (revealUntil, nowMs) => {
  if (!revealUntil) return '';
  const ms = new Date(revealUntil).getTime() - nowMs;
  if (ms <= 0) return '0:00';
  const totalSec = Math.ceil(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const isRevealOpen = (revealUntil, nowMs) =>
  Boolean(revealUntil) && new Date(revealUntil).getTime() > nowMs;

/**
 * SUPER_ADMIN: create / edit / reissue / revoke non-expiring Safety integration API keys.
 * Full key plaintext is visible for 2 minutes after generation only.
 */
const SafetyApiKeysPanel = () => {
  const { showToast } = useToast();
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState('Safety integration');
  const [issuedTo, setIssuedTo] = useState('');
  /** { apiKey, revealUntil, issuedTo, expiresAt, name } while within reveal window */
  const [revealBanner, setRevealBanner] = useState(null);
  const [reissuedBanner, setReissuedBanner] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editIssuedTo, setEditIssuedTo] = useState('');
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const loadKeys = useCallback(async () => {
    try {
      const res = await axios.get('/api/safety/api-keys');
      const payload = res.data;
      const list = Array.isArray(payload) ? payload : (payload?.keys || []);
      setKeys(list);
      const auto = Array.isArray(payload?.reissued) ? payload.reissued : [];
      if (auto.length) {
        setReissuedBanner(auto);
        showToast(
          `${auto.length} expired key(s) auto-reissued — copy within 2 minutes`,
          'warning'
        );
      }
    } catch (err) {
      setKeys([]);
      showToast(err.response?.data?.message || 'Failed to load Safety API keys', 'error');
    }
  }, [showToast]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  // Drop local banners when reveal window ends
  useEffect(() => {
    if (revealBanner?.revealUntil && !isRevealOpen(revealBanner.revealUntil, nowMs)) {
      setRevealBanner(null);
    }
    setReissuedBanner((prev) =>
      prev.filter((r) => isRevealOpen(r.revealUntil, nowMs))
    );
  }, [nowMs, revealBanner]);

  const createKey = async () => {
    if (!issuedTo.trim()) {
      showToast('Issued to is required', 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post('/api/safety/api-keys', {
        name: name.trim() || 'Safety integration',
        issuedTo: issuedTo.trim()
      });
      setRevealBanner({
        apiKey: res.data.apiKey || '',
        revealUntil: res.data.revealUntil,
        issuedTo: res.data.issuedTo,
        expiresAt: res.data.expiresAt,
        name: res.data.name
      });
      showToast('API key created — copy within 2 minutes', 'success');
      await loadKeys();
    } catch (err) {
      showToast(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Failed to create key', 'error');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (k) => {
    setEditingId(k._id);
    setEditName(k.name || '');
    setEditIssuedTo(k.issuedTo || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditIssuedTo('');
  };

  const saveEdit = async (keyId) => {
    if (!editName.trim() || !editIssuedTo.trim()) {
      showToast('Name and Issued to are required', 'error');
      return;
    }
    try {
      await axios.patch(`/api/safety/api-keys/${keyId}`, {
        name: editName.trim(),
        issuedTo: editIssuedTo.trim()
      });
      showToast('Key details updated', 'success');
      cancelEdit();
      await loadKeys();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update key', 'error');
    }
  };

  const reissueKey = async (keyId) => {
    if (!window.confirm('Reissue this key now? The current key stops working immediately; copy the new key within 2 minutes.')) {
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`/api/safety/api-keys/${keyId}/reissue`);
      setRevealBanner({
        apiKey: res.data.apiKey || '',
        revealUntil: res.data.revealUntil,
        issuedTo: res.data.issuedTo,
        expiresAt: res.data.expiresAt,
        name: res.data.name
      });
      showToast('Key reissued — copy within 2 minutes', 'success');
      await loadKeys();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to reissue', 'error');
    } finally {
      setLoading(false);
    }
  };

  const revokeKey = async (keyId) => {
    if (!window.confirm('Revoke this Safety API key? The client system will lose access immediately.')) {
      return;
    }
    try {
      await axios.delete(`/api/safety/api-keys/${keyId}`);
      showToast('API key revoked', 'success');
      setRevealBanner(null);
      await loadKeys();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to revoke', 'error');
    }
  };

  const copyText = async (text, label = 'API key') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} copied`, 'success');
    } catch {
      showToast('Could not copy — select and copy manually', 'warning');
    }
  };

  const activeKeys = useMemo(
    () => keys.filter((k) => k.isActive && !k.revokedAt),
    [keys]
  );
  const revokedKeys = useMemo(
    () => keys.filter((k) => !k.isActive || k.revokedAt),
    [keys]
  );

  const bannerOpen = revealBanner?.apiKey && isRevealOpen(revealBanner.revealUntil, nowMs);

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Keys <strong>do not expire</strong>. Revoke (or Reissue) to invalidate. The full API key is visible for{' '}
        <strong>2 minutes</strong> after generation only, then hidden from everyone.
        Jupiter authenticates the key for read APIs — no Safety-user login is required; view access is defined by the client system.
      </Typography>
      <Stack spacing={2} sx={{ maxWidth: 720 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <TextField
            label="Key name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Issued to"
            value={issuedTo}
            onChange={(e) => setIssuedTo(e.target.value)}
            size="small"
            placeholder="e.g. Client Safety platform"
            sx={{ flex: 1 }}
            required
          />
          <Button variant="contained" onClick={createKey} disabled={loading}>
            Generate API key
          </Button>
        </Stack>

        {bannerOpen && (
          <Alert
            severity="warning"
            action={(
              <Button color="inherit" size="small" onClick={() => copyText(revealBanner.apiKey)}>
                Copy
              </Button>
            )}
          >
            New key — visible for <strong>{formatRemain(revealBanner.revealUntil, nowMs)}</strong> more
            {revealBanner.issuedTo ? <> — Issued to: <strong>{revealBanner.issuedTo}</strong></> : null}
            {' — '}<strong>Does not expire</strong>
            <br />
            <code style={{ wordBreak: 'break-all' }}>{revealBanner.apiKey}</code>
          </Alert>
        )}

        {reissuedBanner.length > 0 && (
          <Alert severity="warning">
            Reissued — copy within 2 minutes (then hidden permanently):
            {reissuedBanner.map((r) => {
              const open = isRevealOpen(r.revealUntil, nowMs);
              return (
                <Box key={r.id} sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    {r.name} — Issued to: <strong>{r.issuedTo || '—'}</strong>
                    {' — '}<strong>Does not expire</strong>
                    {open ? <> — Visible <strong>{formatRemain(r.revealUntil, nowMs)}</strong></> : null}
                  </Typography>
                  {open && r.apiKey ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <code style={{ wordBreak: 'break-all', flex: 1 }}>{r.apiKey}</code>
                      <Button size="small" onClick={() => copyText(r.apiKey)}>Copy</Button>
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Key no longer visible
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Alert>
        )}

        <Typography variant="subtitle2">Active keys</Typography>
        {activeKeys.length === 0 && (
          <Typography variant="body2" color="text.secondary">No active keys.</Typography>
        )}
        {activeKeys.map((k) => {
          const isEditing = editingId === k._id;
          const canShowKey = k.apiKey && isRevealOpen(k.revealUntil, nowMs);
          return (
            <Box
              key={k._id}
              sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              {isEditing ? (
                <Stack spacing={1}>
                  <TextField
                    label="Key name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Issued to"
                    value={editIssuedTo}
                    onChange={(e) => setEditIssuedTo(e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="contained" onClick={() => saveEdit(k._id)}>
                      Save
                    </Button>
                    <Button size="small" onClick={cancelEdit}>Cancel</Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" fontWeight={600}>
                      {k.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {k.keyPrefix}…
                    </Typography>
                    <Chip
                      size="small"
                      label="Active (no expiry)"
                      color="success"
                      variant="outlined"
                    />
                  </Stack>
                  <Typography variant="body2">
                    Issued to: <strong>{k.issuedTo || '—'}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Expiry: <strong>Never</strong>
                  </Typography>
                  {canShowKey ? (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      Full key visible <strong>{formatRemain(k.revealUntil, nowMs)}</strong> —{' '}
                      <code style={{ wordBreak: 'break-all' }}>{k.apiKey}</code>{' '}
                      <Button size="small" onClick={() => copyText(k.apiKey)}>Copy</Button>
                    </Alert>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Full key hidden (2-minute reveal window closed)
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Created {formatWhen(k.createdAt)}
                    {k.lastUsedAt ? ` · Last used ${formatWhen(k.lastUsedAt)}` : ''}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button size="small" onClick={() => startEdit(k)}>Edit</Button>
                    <Button size="small" onClick={() => reissueKey(k._id)} disabled={loading}>
                      Reissue now
                    </Button>
                    <Button size="small" color="error" onClick={() => revokeKey(k._id)}>
                      Revoke
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Box>
          );
        })}

        {revokedKeys.length > 0 && (
          <>
            <Divider />
            <Typography variant="subtitle2">Revoked / replaced</Typography>
            {revokedKeys.map((k) => (
              <Stack key={k._id} spacing={0.25} sx={{ opacity: 0.85 }}>
                <Typography variant="body2" color="text.secondary">
                  {k.name} — {k.keyPrefix}… — Issued to: {k.issuedTo || '—'}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip size="small" label={k.revokeReason === 'ADMIN_REISSUE' ? 'Reissued' : 'Revoked'} variant="outlined" />
                  <Typography variant="caption" color="text.secondary">
                    {k.revokedAt ? `Revoked ${formatWhen(k.revokedAt)}` : ''}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </>
        )}
      </Stack>
    </Box>
  );
};

export default SafetyApiKeysPanel;
